import React, { useEffect } from "react";
import { Image, View, StyleSheet } from "react-native";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import {
  CardTitleText,
  DocumentRow,
  MoonFormField,
  TextActionButton,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useNavigation } from "@react-navigation/native";
import { Pill, Clock, FileText, Plus } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { MedicationsVaccinesStackScreenProps } from "../MedicationsVaccinesNavigator";
import {
  MedicationDetail,
  useMedicationsVaccines,
} from "../MedicationsVaccinesContext";
import { getMedicationProductLabelKey } from "../options";
import {
  reconcileMedicationDetails,
  removeMedicationDetail,
} from "../medicationState";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

export function MedicationDetailsScreen() {
  const t = useTranslations();
  const { state, updateState } = useMedicationsVaccines();
  const theme = useMedicalHistoryTheme();
  const navigation =
    useNavigation<
      MedicationsVaccinesStackScreenProps<"MedicationDetails">["navigation"]
    >();

  useEffect(() => {
    const reconciled = reconcileMedicationDetails(
      state.selectedProducts,
      state.medicationDetails,
      state.otherMedicationText,
    );

    if (reconciled) {
      const idsToDelete =
        reconciled.droppedPersistedIds.length > 0
          ? Array.from(
              new Set([
                ...(state.medicationIdsToDelete || []),
                ...reconciled.droppedPersistedIds,
              ]),
            )
          : state.medicationIdsToDelete;
      updateState({
        medicationDetails: reconciled.medicationDetails,
        ...(reconciled.consumedOtherMedicationText
          ? { otherMedicationText: "" }
          : {}),
        ...(idsToDelete !== state.medicationIdsToDelete
          ? { medicationIdsToDelete: idsToDelete }
          : {}),
      });
    }
  }, [
    state.selectedProducts,
    state.medicationDetails,
    state.medicationIdsToDelete,
    state.otherMedicationText,
    updateState,
  ]);

  const handleChange = (index: number, updates: Partial<MedicationDetail>) => {
    const next = state.medicationDetails.map((detail, i) =>
      i === index ? { ...detail, ...updates } : detail,
    );
    updateState({ medicationDetails: next });
  };

  const handleRemove = (index: number) => {
    updateState(
      removeMedicationDetail(
        state.selectedProducts,
        state.medicationDetails,
        state.medicationIdsToDelete,
        index,
      ),
    );
  };

  const handleAddAnother = () => {
    updateState({
      medicationDetails: [
        ...state.medicationDetails,
        { category: null, name: "", brand: null, frequency: null, notes: null },
      ],
    });
  };

  const handleSave = () => {
    navigation.navigate("VaccinesSelect");
  };
  const hasMissingMedicationName = state.medicationDetails.some(
    (detail) => !detail.name?.trim(),
  );

  const fallbackTitle = t(
    "med_history.medical.medications_vaccines.medication_card_fallback",
  );

  return (
    <QuestionLayout
      title={t(
        "med_history.medical.medications_vaccines.medication_details_title",
      )}
      subtitle={t(
        "med_history.medical.medications_vaccines.medication_name_required_other_details_optional",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={
        state.medicationDetails.length === 0 || hasMissingMedicationName
      }
      saveLabel={t("med_history.medical.shared.continue")}
    >
      {state.medicationDetails.map((detail, index) => {
        const categoryLabel = detail.category
          ? t(getMedicationProductLabelKey(detail.category))
          : fallbackTitle;
        const isRemovable =
          detail.category == null || detail.category === "other";

        return (
          <View key={index} style={styles.cardWrapper}>
            <View style={[cardSurface(), styles.detailCard]}>
              <View style={styles.cardHeader}>
                <CardTitleText>
                  {detail.category == null ? fallbackTitle : categoryLabel}
                </CardTitleText>
                {isRemovable ? (
                  <TextActionButton
                    label={t("med_history.medical.shared.delete")}
                    action="med_history.medical.shared.delete"
                    tone="destructive"
                    onPress={() => handleRemove(index)}
                    style={{ paddingHorizontal: 0, paddingVertical: 0 }}
                  />
                ) : null}
              </View>
              <MoonFormField
                label={t(
                  "med_history.medical.medications_vaccines.medication_name_label",
                )}
                labelIcon={Pill}
              >
                <MedicalHistoryTextField
                  placeholder={t(
                    "med_history.medical.medications_vaccines.medication_name_placeholder",
                  )}
                  value={detail.name ?? ""}
                  onChangeText={(text) => handleChange(index, { name: text })}
                  appearance={theme.inputAppearance}
                  style={{ width: "100%" }}
                />
              </MoonFormField>

              <MoonFormField
                label={t(
                  "med_history.medical.medications_vaccines.frequency_label",
                )}
                labelIcon={Clock}
              >
                <MedicalHistoryTextField
                  placeholder={t(
                    "med_history.medical.medications_vaccines.frequency_placeholder",
                  )}
                  value={detail.frequency ?? ""}
                  onChangeText={(text) =>
                    handleChange(index, { frequency: text })
                  }
                  appearance={theme.inputAppearance}
                  style={{ width: "100%" }}
                />
              </MoonFormField>

              <MoonFormField
                label={t(
                  "med_history.medical.medications_vaccines.comments_label",
                )}
                labelIcon={FileText}
              >
                <MedicalHistoryTextField
                  placeholder={t(
                    "med_history.medical.medications_vaccines.comments_placeholder",
                  )}
                  value={detail.notes ?? ""}
                  onChangeText={(text) => handleChange(index, { notes: text })}
                  multiline
                  numberOfLines={3}
                  appearance={theme.inputAppearance}
                  style={{ width: "100%" }}
                />
              </MoonFormField>
            </View>
          </View>
        );
      })}
      <DocumentRow
        onPress={handleAddAnother}
        accessibilityLabel={t(
          "med_history.medical.medications_vaccines.add_another_medication",
        )}
        leading={
          <Image
            source={medicationPillIllustration}
            resizeMode="contain"
            style={styles.addIllustration}
          />
        }
        title={t(
          "med_history.medical.medications_vaccines.add_another_medication",
        )}
        trailing={
          <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
        }
        showChevron={false}
        style={styles.addRow}
      />
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  cardWrapper: {
    marginBottom: 8,
  },
  detailCard: {
    gap: 16,
    padding: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});

export default MedicationDetailsScreen;
